import React from 'react';

/**
 * A list's stages as count boxes that are also its tabs — the number IS the
 * button, so a page does not say the same thing twice (a tile row above a tab
 * row). Same look as the support queue's lanes (`c-q-lane`).
 *
 * Order the stages by what needs acting on first; the catch-all "All" goes
 * last. On a phone the strip scrolls sideways so the list stays on screen.
 *
 * stages: [{ key, label, count, hint?, tone?, late?, marks? }]
 *   tone  — blue | amber | violet | red | green | grey (the top edge)
 *   late  — a red "N late" marker; 0 or absent shows nothing
 *   marks — several markers: [{ n, label, tone: 'red' | 'amber' }] (n 0 = hidden)
 *   sub   — a second figure under the count (e.g. "42 laptops" under 12 orders)
 * `rules` — one plain line under the boxes saying how late is counted, so the
 * team and the owner read the same definition.
 * A count of null/undefined renders "…" (still loading), never a fake 0.
 */
export default function StageTiles({ stages = [], value, onChange, label = 'Stages', rules }) {
  const tiles = (
    <div className="c-stages" role="tablist" aria-label={label} style={{ '--n': stages.length }}>
      {stages.map((s) => (
        <button
          key={s.key || 'all'}
          type="button"
          role="tab"
          aria-selected={value === s.key}
          className={`c-q-lane is-${s.tone || 'grey'}${value === s.key ? ' is-on' : ''}`}
          onClick={() => onChange(s.key)}
        >
          <span className="c-q-lane-l">{s.label}</span>
          <b>{s.count == null ? '…' : Number(s.count).toLocaleString('en-IN')}</b>
          {s.sub && <span className="c-q-lane-sub">{s.sub}</span>}
          {s.hint && <span className="c-q-lane-h">{s.hint}</span>}
          {(s.late > 0 || (s.marks || []).some((m) => m.n > 0)) && (
            <span className="c-stage-marks">
              {s.late > 0 && <span className="c-q-lane-late">{s.late} late</span>}
              {(s.marks || []).filter((m) => m.n > 0).map((m) => (
                <span key={m.label} className={`c-q-lane-late${m.tone === 'amber' ? ' is-amber' : ''}`}>{m.n} {m.label}</span>
              ))}
            </span>
          )}
        </button>
      ))}
    </div>
  );
  if (!rules) return tiles;
  return (
    <div style={{ display: 'grid', gap: '6px' }}>
      {tiles}
      <p className="c-stage-rules">{rules}</p>
    </div>
  );
}
